import { zodResolver } from '@hookform/resolvers/zod'
import { StartTaskDtoSchema } from '@myapp/contract'
import { AxiosError } from 'axios'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { useStartTask } from '../../../../entities/task'
import { DurationSpinner } from '../../../../shared/ui/controls/DurationSpinner/DurationSpinner'
import Modal from '../../../../shared/ui/overlay/Modal/Modal'

const FormSchema = StartTaskDtoSchema

type FormValues = z.infer<typeof FormSchema>

type StartTaskErrorBody = {
  error?: string
  message?: string
  meta?: { openType?: 'appointment' | 'task' } | null
}

function getStartTaskErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    const data = error.response?.data as StartTaskErrorBody | undefined
    switch (data?.error) {
      case 'TASK_NOT_FOUND':
        return 'Aufgabe nicht gefunden.'
      case 'TASK_FORBIDDEN':
        return 'Kein Zugriff auf diese Aufgabe.'
      case 'TASK_UNASSIGNED':
        return 'Die Aufgabe ist keinem Techniker zugewiesen.'
      case 'ANOTHER_ITEM_IN_PROGRESS':
        return data.meta?.openType === 'appointment'
          ? 'Ein anderer Termin ist bereits in Bearbeitung. Bitte zuerst abschließen.'
          : 'Eine andere Aufgabe ist bereits in Bearbeitung. Bitte zuerst abschließen.'
    }
    if (error.response && error.response.status >= 500) {
      return 'Interner Serverfehler. Bitte später erneut versuchen.'
    }
  }
  return 'Arbeit konnte nicht gestartet werden. Bitte erneut versuchen.'
}

interface Props {
  show: boolean
  setShow: (show: boolean) => void
  taskId: number
  plannedTravelDuration: number
  onSuccess: () => void
}

export function StartTaskModal({
  show,
  setShow,
  taskId,
  plannedTravelDuration,
  onSuccess,
}: Props) {
  const start = useStartTask(taskId)

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { actualTravelDuration: plannedTravelDuration },
  })

  useEffect(() => {
    if (show) {
      reset({ actualTravelDuration: plannedTravelDuration })
      start.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, plannedTravelDuration, reset])

  const onSubmit = (values: FormValues) => {
    start.mutate({ actualTravelDuration: values.actualTravelDuration }, { onSuccess })
  }

  return (
    <Modal
      show={show}
      setShow={setShow}
      title="Arbeit starten"
      submitButton={{ onSubmit: handleSubmit(onSubmit), title: 'Arbeit starten', isPending: start.isPending }}
    >
      <div className="flex flex-col gap-4 w-full">
        <Controller
          name="actualTravelDuration"
          control={control}
          render={({ field }) => (
            <DurationSpinner
              label="Anfahrt (min)"
              value={field.value}
              onChange={field.onChange}
              error={errors.actualTravelDuration?.message}
            />
          )}
        />
        {start.isError && (
          <p role="alert" className="text-sm text-red-500 font-medium">
            {getStartTaskErrorMessage(start.error)}
          </p>
        )}
      </div>
    </Modal>
  )
}
